import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { ReactComponent as EditIcon } from '../../../../assets/SVG/editIconSvg.svg';
import { getButtonStates } from '../../../../utils/permissionService';
import { useTranslation } from 'react-i18next';

interface GrantedRight {
  Name: string;
  action: string[];
  type: string;
}


interface ViewModeProps {
  grantedRights: GrantedRight;
  userName?: string | null;
  onEdit?: () => void;
}

const ViewMode: React.FC<ViewModeProps> = ({ grantedRights, userName, onEdit }) => {
  const { t } = useTranslation('user_config');
  const isArray = Array.isArray(grantedRights);
  const { canEdit, canAdd } = getButtonStates('Settings', 'Configuration', 'User');
  const permissionKeys = [
    { label: t('UserRights.Male'), key: 'male' },
    { label: t('UserRights.Female'), key: 'female' },
    { label: t('UserRights.Add'), key: 'add' },
    { label: t('UserRights.Edit'), key: 'edit' },
    { label: t('UserRights.View'), key: 'view' },
    { label: t('UserRights.Print'), key: 'print' },
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getTypeAndColor = (row: any) => {
    if (row.type === 'module') return { label: 'Module', color: '#2F80ED' };
    if (row.type === 'category') return { label: 'Category', color: '#27AE60' };
    return { label: 'Subcategory', color: '#F2C94C' };
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ bgcolor: '#FAFAFA', borderRadius: 3, px: 4, py: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {userName}
        </Typography>
        {(canEdit || canAdd) &&(

          <Box sx={{ color: '#4285F4', cursor: 'pointer', fontSize: 28 }} onClick={onEdit}>
            {/* <svg width="24" height="24" fill="none" stroke="#4285F4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19.5l-4 1 1-4L16.5 3.5Z"/></svg> */}
            <EditIcon />
          </Box>
        )}
      </Box>
      {isArray ? (
        <TableContainer component={Paper} sx={{ boxShadow: 'none', borderRadius: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#FAFAFA' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: 18, border: 'none', width: 200 }}></TableCell>
                {permissionKeys.map((col) => (
                  <TableCell key={col.key} align="center" sx={{ fontWeight: 600, fontSize: 18, border: 'none' }}>{col.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {grantedRights.map((row) => 
              {
                const { color } = getTypeAndColor(row);
                // Linear gradient backgrounds for each type
                let gradient = '#fff';
                if (row.type === 'module') gradient = 'linear-gradient(90deg, #EAF3FF 0%, #fff 100%)';
                else if (row.type === 'category') gradient = 'linear-gradient(90deg, #E6F9EC 0%, #fff 100%)';
                else gradient = 'linear-gradient(90deg, #FFFBE6 0%, #fff 100%)';

                return(
                  <TableRow key={row.Name}>
                    <TableCell sx={{ border: 'none', fontWeight: 500, fontSize: 16, color: '#222', pl: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: 200, background: gradient, mr: 2, borderRadius: 2 }}>
                        <Box sx={{ width: 6, height: 40, borderRadius: 2, bgcolor: color, mr: 2 }} />
                        <Typography sx={{ fontWeight: 500, fontSize: 16, fontFamily: 'Montserrat' }}>
                          {row.name_label || row.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    {permissionKeys.map((col) => {
                      if (col.key === 'male') {
                        return (
                          <TableCell key={`male-${row.Name}`} align="center" sx={{ border: 'none', fontWeight: 500, fontSize: 15, color: '#2F80ED' }}>
                            {row.male === true && <CheckIcon sx={{ color: '#38B24D', fontSize: 28 }} />}
                          </TableCell>
                        );
                      }
                      if (col.key === 'female') {
                        return (
                          <TableCell key={`female-${row.Name}`} align="center" sx={{ border: 'none', fontWeight: 500, fontSize: 15, color: '#E25E3E' }}>
                            {row.female === true && <CheckIcon sx={{ color: '#38B24D', fontSize: 28 }} />}
                          </TableCell>
                        );
                      }
                      return (
                        <TableCell key={`${col.key}-${row.Name}`} align="center" sx={{ border: 'none' }}>
                          {Array.isArray(row.actions) && row.actions.includes(col.key) && (
                            <CheckIcon sx={{ color: '#38B24D', fontSize: 28 }} />
                          )}
                          {(!row.actions && Array.isArray(row.action) && row.action.includes(col.key)) && (
                            <CheckIcon sx={{ color: '#38B24D', fontSize: 28 }} />
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              }
              
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ p: 4, textAlign: 'center', color: '#757575' }}>
          <Typography variant="body1">No permissions found or invalid format.</Typography>
        </Box>
      )}
    </Box>
  );
};

export default ViewMode;
